/* ==========================================================================
   Mairaz - Express Delivery fee synchronisation
   --------------------------------------------------------------------------
   A line-item property does not charge money. The Delivery Option property
   written by snippets/mairaz-delivery-options.liquid records the customer's
   choice; this script keeps a real, visible, chargeable cart line in step
   with it.

   RULE
     quantity of the "Express Delivery Upgrade" line
       = sum of the quantities of every OTHER cart line whose
         properties["Delivery Option"] starts with "Express Delivery"

   One Express item at quantity 2 needs 2. An Express line plus a Standard
   line needs only the Express units. When the last Express item goes the
   required quantity is 0, which removes the line.

   --------------------------------------------------------------------------
   Every guard below exists because of a specific failure that was found by
   simulating it. Please read before simplifying.

   IDEMPOTENT. sync() reads /cart.js, computes the required quantity and
   writes ONLY when it differs, so a re-trigger over a correct cart is free.

   SINGLE-FLIGHT WITH A REQUEUE LOOP. Mid-flight callers get the SAME pending
   promise, so awaiting sync() always means "the cart has settled".

   EVERY WRITE IS VERIFIED BY RE-READING. A 200 does not prove the quantity
   landed - Shopify clamps for inventory rules and per-order limits. A cycle
   is capped at MAX_WRITES_PER_CYCLE; beyond that it latches `stalled`.

   A STALLED CYCLE NEVER RELOADS. If the cart cannot reach the required
   quantity, reloading would re-run the same doomed write on every load. A
   stalled cycle refreshes the cart count only.

   FOREIGN-WRITE BARRIER, ON WRITES *AND* ON THE CHECKOUT READ. Dawn
   addresses cart lines by INDEX, so inserting or removing the fee line while
   one of its /cart/change requests is in flight can make it mutate the wrong
   line: each of our writes waits for the foreign in-flight count to reach
   zero, bounded by BARRIER_MAX_WAIT_MS. The checkout click waits too, before
   it reads - a read taken mid-write returns the pre-write cart, and the
   customer checks out with Express selected and no fee. An observed request
   that never settles is released by a watchdog after FETCH_TIMEOUT_MS.

   NOTHING BLOCKS CHECKOUT. Every wait is bounded, every fetch times out even
   without AbortController, `disabled` is time-boxed rather than permanent,
   the checkout path bypasses it, and the replayed click re-resolves its
   target in case the theme re-rendered the button while we waited. Buttons
   that cannot be replayed (shadow-DOM wallet hosts) are never intercepted at
   all, a replay is not re-intercepted for 2s, and after MAX_INTERCEPTS the
   listener steps aside entirely. If synchronisation fails the click is
   replayed anyway: an uncharged fee costs less than a customer who cannot
   check out.

   TRIGGERS ARE DELAYED, NEVER CANCELLED. The debounce is re-armed by each
   cart event but fires unconditionally once DEBOUNCE_MAX_WAIT_MS has passed
   since the first event of the burst, so a theme writing faster than the
   debounce window cannot starve synchronisation.

   RENDERING IS NEVER SUPPRESSED BY SOMEONE ELSE'S PREFERENCE. Only a cycle
   in which EVERY caller asked for no render skips it, so a checkout click
   joining a normal cycle cannot leave the cart page stale.

   KNOWN LIMIT. Only window.fetch is observed. An app mutating the cart by
   XMLHttpRequest is caught later - visibilitychange, pageshow, next page
   load, or the checkout click - rather than immediately.
   ========================================================================== */

(function () {
  'use strict';

  function log(message, detail) {
    if (window.console && console.warn) console.warn('[mairaz-express-fee] ' + message, detail || '');
  }

  var CFG = window.mairazExpressFee;
  if (!CFG) return;
  if (window.mairazExpressFeeReady) return;
  window.mairazExpressFeeReady = true;

  var VARIANT_ID = Number(CFG.variantId);
  if (!CFG.variantId || !isFinite(VARIANT_ID) || VARIANT_ID <= 0) {
    log('variantId is missing or not a usable numeric variant id; the Express fee is INACTIVE.', CFG.variantId);
    return;
  }

  var PROP = CFG.propertyName || 'Delivery Option';
  var PREFIX = String(CFG.expressPrefix || 'Express Delivery').trim().toLowerCase();
  var R = CFG.routes || {};
  var CART_JS = (R.cart || '/cart') + '.js';
  var ADD_JS = (R.add || '/cart/add') + '.js';
  var CHANGE_JS = (R.change || '/cart/change') + '.js';
  var UPDATE_JS = (R.update || '/cart/update') + '.js';

  var MAX_CONSECUTIVE_FAILURES = 3;
  var DISABLED_FOR_MS = 30000;
  var MAX_WRITES_PER_CYCLE = 3;
  var MAX_COLLAPSES_PER_CYCLE = 8;
  var FETCH_TIMEOUT_MS = 10000;
  var BARRIER_MAX_WAIT_MS = 3000;
  var CHECKOUT_MAX_WAIT_MS = 6000;
  var DEBOUNCE_MS = 250;
  var DEBOUNCE_MAX_WAIT_MS = 1000;
  var MAX_INTERCEPTS = 3;

  /* Captured BEFORE the observer is installed - our traffic bypasses it. */
  var nativeFetch = window.fetch && window.fetch.bind(window);
  if (!nativeFetch) return;

  var busy = false;
  var requeue = false;
  var pending = null;
  var renderWanted = true;
  var failures = 0;
  var disabledUntil = 0;
  var debounceId = null;
  var replayingClick = false;
  var replayGuardUntil = 0;
  var intercepts = 0;

  function now() {
    return new Date().getTime();
  }

  function isDisabled() {
    return disabledUntil > 0 && now() < disabledUntil;
  }

  /* ---------------------------------------------------------------- utils */

  function settle(promise, ms, fallback, onTimeout) {
    return new Promise(function (resolve) {
      var done = false;
      var timer = window.setTimeout(function () {
        if (done) return;
        done = true;
        if (onTimeout) {
          try { onTimeout(); } catch (error) { /* never let cleanup break the wait */ }
        }
        resolve(fallback);
      }, ms);
      function finish(value) {
        if (done) return;
        done = true;
        window.clearTimeout(timer);
        resolve(value);
      }
      promise.then(finish, function () { finish(fallback); });
    });
  }

  function request(url, body, rawJson) {
    var controller = null;
    var options = {
      headers: rawJson ? {} : { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store'
    };

    if (body) {
      options.method = 'POST';
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }

    if (typeof AbortController === 'function') {
      controller = new AbortController();
      options.signal = controller.signal;
    }

    var live = nativeFetch(url, options).then(function (response) {
      if (!response.ok) throw new Error(url + ' -> ' + response.status);
      return rawJson ? response.text().then(function (t) { return JSON.parse(t); }) : response.json();
    });

    /* Timeout applies even where AbortController is unavailable, so a hung
       request can never latch `busy`. */
    return settle(
      live.then(
        function (value) { return { ok: true, value: value }; },
        function (error) { return { ok: false, error: error }; }
      ),
      FETCH_TIMEOUT_MS,
      { ok: false, error: new Error('timed out: ' + url) },
      function () { if (controller) { try { controller.abort(); } catch (error) { /* ignore */ } } }
    ).then(function (result) {
      if (result.ok) return result.value;
      throw result.error;
    });
  }

  /* ------------------------------------------------- foreign-write barrier */

  var foreignInFlight = 0;
  var foreignWaiters = [];

  function foreignRelease() {
    foreignInFlight -= 1;
    if (foreignInFlight > 0) return;
    foreignInFlight = 0;
    var waiters = foreignWaiters;
    foreignWaiters = [];
    for (var i = 0; i < waiters.length; i++) waiters[i]();
  }

  function foreignSettled(maxWait) {
    if (foreignInFlight <= 0) return Promise.resolve(true);
    var waiter;
    var promise = new Promise(function (resolve) {
      waiter = function () { resolve(true); };
      foreignWaiters.push(waiter);
    });
    return settle(promise, maxWait || BARRIER_MAX_WAIT_MS, false, function () {
      var index = foreignWaiters.indexOf(waiter);
      if (index !== -1) foreignWaiters.splice(index, 1);
    });
  }

  function guardedWrite(url, body) {
    return foreignSettled().then(function () { return request(url, body); });
  }

  /* ------------------------------------------------------------ cart logic */

  function isExpress(item) {
    var props = item.properties;
    if (!props) return false;
    var value = props[PROP];
    if (typeof value !== 'string') return false;
    var normalised = value.trim().toLowerCase();
    if (normalised.slice(0, PREFIX.length) !== PREFIX) return false;
    /* Require a word boundary so a future "Express Deliveryx" option cannot
       silently inherit the charge. */
    var next = normalised.charAt(PREFIX.length);
    return next === '' || !/[a-z0-9]/.test(next);
  }

  function inspect(cart) {
    var state = { required: 0, feeLines: [] };
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (Number(item.variant_id) === VARIANT_ID) {
        state.feeLines.push(item);
        continue;
      }
      if (isExpress(item)) state.required += item.quantity;
    }
    state.current = state.feeLines.length ? state.feeLines[0].quantity : 0;
    return state;
  }

  function onCartPage() {
    return !!document.getElementById('main-cart-items');
  }

  function reloadAllowed() {
    var storage;
    try {
      storage = window.sessionStorage;
      if (!storage) return false;
    } catch (error) {
      return false;
    }

    var data = null;
    try {
      var raw = storage.getItem('mrzExpressFeeReload');
      if (raw) data = JSON.parse(raw);
    } catch (error) {
      data = null; /* corrupt value: repaired below, never latched */
    }
    if (!data || typeof data.n !== 'number' || typeof data.t !== 'number') data = { n: 0, t: 0 };

    var stamp = now();
    var allowed;
    if (stamp - data.t > 10000) {
      data = { n: 1, t: stamp };
      allowed = true;
    } else {
      data.n += 1;
      allowed = data.n <= 2;
      /* data.t is deliberately NOT refreshed, so sustained activity cannot
         hold the window open forever. */
    }

    try {
      storage.setItem('mrzExpressFeeReload', JSON.stringify(data));
    } catch (error) {
      return false;
    }
    return allowed;
  }

  /* ------------------------------------------------------- header cart UI */

  /* Dawn renders the header bubble from the response of ITS OWN cart write.
     That response was captured before our Express fee line existed, so when
     Dawn's render lands after ours the bubble shows a pre-fee count and stays
     stale until the next page load. We therefore re-assert the bubble against
     the authoritative item_count from the last /cart.js read of the cycle.

     This is bounded (VERIFY_ATTEMPTS extra section renders, no cart writes at
     all), so it can never loop, never double-write the fee and never reload. */
  var authoritativeCount = null;
  var verifyId = null;
  var VERIFY_ATTEMPTS = 2;
  var VERIFY_DELAY_MS = 350;

  function bubbleCount() {
    var host = document.getElementById('cart-icon-bubble');
    if (!host) return null;
    var bubble = host.querySelector('.cart-count-bubble');
    if (!bubble) return 0;
    var node = bubble.querySelector('[aria-hidden="true"]') || bubble;
    var digits = String(node.textContent || '').replace(/[^0-9]/g, '');
    return digits === '' ? 0 : parseInt(digits, 10);
  }

  function renderTargets() {
    var t = [];
    if (document.getElementById('cart-icon-bubble')) t.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    if (document.getElementById('cart-live-region-text')) t.push({ id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' });
    var items = document.getElementById('main-cart-items');
    if (items && items.dataset && items.dataset.id) t.push({ id: 'main-cart-items', section: items.dataset.id, selector: '.js-contents' });
    var footer = document.getElementById('main-cart-footer');
    if (footer && footer.dataset && footer.dataset.id) t.push({ id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' });
    return t;
  }

  function renderCartSections() {
    var targets = renderTargets();
    if (!targets.length) return Promise.resolve(false);
    var names = [];
    for (var n = 0; n < targets.length; n++) {
      if (names.indexOf(targets[n].section) === -1) names.push(targets[n].section);
    }
    /* ?sections= only renders sections belonging to the current template, so
       cart-icon-bubble came back missing on product pages - the reason the
       header count stayed stale until reload. The Cart AJAX API renders any
       section by name, the same mechanism Dawn itself uses. No cart field is
       sent, so cart contents are untouched. */
    return request(UPDATE_JS, { sections: names.join(','), sections_url: window.location.pathname })
      .then(function (payload) {
        var sections = payload && payload.sections;
        if (!sections) return false;
        if (typeof payload.item_count === 'number') authoritativeCount = payload.item_count;

        /* Dawn toggles these when IT changes the cart; when the fee sync is the
           thing that empties the cart we have to do the same, otherwise the cart
           page keeps rendering a totals block instead of its empty state. */
        if (typeof payload.item_count === 'number') {
          var isEmpty = payload.item_count === 0;
          var cartItemsEl = document.querySelector('cart-items') || document.getElementById('main-cart-items');
          if (cartItemsEl) cartItemsEl.classList.toggle('is-empty', isEmpty);
          var cartFooterEl = document.getElementById('main-cart-footer');
          if (cartFooterEl) cartFooterEl.classList.toggle('is-empty', isEmpty);
          var cartDrawerEl = document.querySelector('cart-drawer');
          if (cartDrawerEl) cartDrawerEl.classList.toggle('is-empty', isEmpty);
        }
        var rendered = false;
        for (var i = 0; i < targets.length; i++) {
          var target = targets[i];
          var html = sections[target.section];
          var host = document.getElementById(target.id);
          if (!html || !host) continue;
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var source = parsed.querySelector(target.selector);
          if (!source) continue;
          var replace = host.querySelector(target.selector) || host;
          replace.innerHTML = source.innerHTML;
          rendered = true;
        }
        return rendered;
      })
      .catch(function () { return false; });
  }

  function verifyCartUI(attempt) {
    if (verifyId) { window.clearTimeout(verifyId); verifyId = null; }
    if (attempt > VERIFY_ATTEMPTS) return;
    if (authoritativeCount === null) return;
    verifyId = window.setTimeout(function () {
      verifyId = null;
      var shown = bubbleCount();
      if (shown === null || shown === authoritativeCount) return;
      renderCartSections().then(function () { verifyCartUI(attempt + 1); });
    }, VERIFY_DELAY_MS);
  }

  function refreshCartUI() {
    renderCartSections().then(function () { verifyCartUI(1); });
  }

  function afterChange(mayReload) {
    try {
      /* renderCartSections() now refreshes cart lines, totals, live region and
         the header bubble in place, so the cart page no longer needs a reload. */
      refreshCartUI();
    } catch (error) {
      /* Rendering must never take the sync down with it. */
    }
  }

  /* ----------------------------------------------------------------- sync */

  function sync(options) {
    options = options || {};

    if (isDisabled() && !options.force) return Promise.resolve(false);

    if (busy) {
      requeue = true;
      if (!options.noRender) renderWanted = true;
      return pending || Promise.resolve(false);
    }

    busy = true;
    requeue = false;
    renderWanted = !options.noRender;

    var changedAny = false;
    var stalled = false;
    var writes = 0;
    var collapses = 0;

    function finishCycle(didStall) {
      if (didStall) stalled = true;
      if (requeue && !stalled) {
        requeue = false;
        return pass();
      }
      return changedAny;
    }

    function pass() {
      return request(CART_JS).then(function (cart) {
        var state = inspect(cart);

        /* Authoritative for the header bubble re-assert. */
        authoritativeCount = typeof cart.item_count === 'number' ? cart.item_count : null;

        /* Duplicate fee lines have their OWN budget, so collapsing them can
           never consume the writes needed to fix the quantity. */
        if (state.feeLines.length > 1) {
          if (collapses >= MAX_COLLAPSES_PER_CYCLE) return finishCycle(true);
          collapses += 1;
          return guardedWrite(CHANGE_JS, {
            id: state.feeLines[state.feeLines.length - 1].key,
            quantity: 0
          }).then(function () {
            changedAny = true;
            return pass();
          });
        }

        if (state.current === state.required) return finishCycle(false);

        if (writes >= MAX_WRITES_PER_CYCLE) {
          log(
            'the cart would not accept the required Express fee quantity after ' +
              writes +
              ' attempts (wanted ' +
              state.required +
              ', cart holds ' +
              state.current +
              '). Giving up for this cycle; no reload will be requested.'
          );
          return finishCycle(true);
        }

        writes += 1;
        var write = state.feeLines.length
          ? guardedWrite(CHANGE_JS, { id: state.feeLines[0].key, quantity: state.required })
          : guardedWrite(ADD_JS, { items: [{ id: VARIANT_ID, quantity: state.required }] });

        /* Always re-read: a 200 does not prove the quantity landed. */
        return write.then(function () {
          changedAny = true;
          return pass();
        });
      });
    }

    function release() {
      busy = false;
      pending = null;
      var hadRequeue = requeue;
      requeue = false;
      /* A trigger that arrived during a stalled cycle must not be lost. */
      if (hadRequeue && stalled) schedule();
    }

    try {
      pending = pass()
        .then(function (result) {
          var render = renderWanted;
          release();
          failures = 0;
          disabledUntil = 0;
          /* Rendered once, at the end of the cycle. A stalled cycle refreshes
             but never reloads - see the header comment. */
          /* A cycle that changed the fee refreshes (and may reload on the
             cart page). A cycle that changed nothing still re-renders the
             header sections once, so a foreign cart write can never leave a
             stale count. No cart writes happen here, so this cannot loop. */
          if (render) {
            if (result) afterChange(!stalled);
            else refreshCartUI();
          }
          return result;
        })
        .catch(function (error) {
          var render = renderWanted;
          release();
          failures += 1;
          if (failures >= MAX_CONSECUTIVE_FAILURES) {
            disabledUntil = now() + DISABLED_FOR_MS;
            log('paused for ' + DISABLED_FOR_MS / 1000 + 's after ' + failures + ' consecutive failures. Checkout will still force one attempt.', error);
            failures = 0;
          } else {
            log('synchronisation attempt ' + failures + ' of ' + MAX_CONSECUTIVE_FAILURES + ' failed; will retry on the next cart event.', error);
          }
          /* A write may well have landed before the failure - do not leave the
             page showing stale totals. */
          if (changedAny && render) afterChange(false);
          return false;
        });
    } catch (error) {
      /* A third-party fetch wrapper can throw synchronously. Never leave
         `busy` latched - that would wedge every later sync. */
      release();
      failures += 1;
      log('synchronisation could not start.', error);
      return Promise.resolve(false);
    }

    return pending;
  }

  /* A purely trailing debounce is STARVED by a theme that writes to the cart
     faster than the window - re-arming on every write meant a customer
     hammering the quantity spinner got no synchronisation at all while they
     were doing it. The burst stamp forces one through after
     DEBOUNCE_MAX_WAIT_MS, so activity delays a sync but can never cancel it. */
  var burstStartedAt = 0;

  function schedule() {
    var stamp = now();
    if (!burstStartedAt) burstStartedAt = stamp;
    if (debounceId) window.clearTimeout(debounceId);
    var wait = stamp - burstStartedAt >= DEBOUNCE_MAX_WAIT_MS ? 0 : DEBOUNCE_MS;
    debounceId = window.setTimeout(function () {
      debounceId = null;
      burstStartedAt = 0;
      sync();
    }, wait);
  }

  /* ------------------------------------------------------------- observer */

  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var previousFetch = window.fetch;

  window.fetch = function (input) {
    var url = '';
    try {
      url = typeof input === 'string' ? input : (input && input.url) || '';
    } catch (error) {
      url = '';
    }

    var watched = !!url && CART_WRITE.test(url);

    /* Exactly one release per observed request, whatever happens to it. A
       foreign fetch that never settles used to pin foreignInFlight above zero
       for the rest of the page load, so every later write of ours paid the
       full BARRIER_MAX_WAIT_MS before it went out. The watchdog bounds that;
       the `released` flag makes double-release impossible, which would
       otherwise let a write slip past the barrier. */
    var released = false;
    var watchdog = null;
    function releaseOnce() {
      if (released) return;
      released = true;
      if (watchdog) window.clearTimeout(watchdog);
      watchdog = null;
      foreignRelease();
    }

    if (watched) {
      foreignInFlight += 1;
      watchdog = window.setTimeout(releaseOnce, FETCH_TIMEOUT_MS);
    }

    var result;
    try {
      result = previousFetch.apply(this, arguments);
    } catch (error) {
      if (watched) releaseOnce();
      throw error;
    }

    if (watched && result && typeof result.then === 'function') {
      result.then(
        function () { releaseOnce(); schedule(); },
        function () { releaseOnce(); }
      );
    } else if (watched) {
      releaseOnce();
    }

    return result;
  };

  /* ------------------------------------------------------------- checkout */

  var CHECKOUT_SELECTOR = [
    '[name="checkout"]',
    'a[href$="/checkout"]',
    'a[href*="/checkout?"]',
    'a[href*="/checkouts/"]',
    'shopify-accelerated-checkout',
    '.shopify-payment-button__button',
    '[data-testid="Checkout-button"]'
  ].join(', ');

  /* The re-resolve fallback must not land on something that cannot be
     activated. document.querySelector returns the first match in DOCUMENT
     order, which on a cart page with wallet buttons above the form is a
     shadow-DOM host - clicking it does nothing and no navigation follows. */
  var REPLAY_SELECTOR = [
    '[name="checkout"]',
    'a[href$="/checkout"]',
    'a[href*="/checkout?"]',
    'a[href*="/checkouts/"]'
  ].join(', ');

  function stillInDocument(node) {
    if (!node) return false;
    if (typeof node.isConnected === 'boolean') return node.isConnected;
    return document.documentElement.contains(node);
  }

  document.addEventListener(
    'click',
    function (event) {
      /* replayGuardUntil, not just a flag cleared on the next macrotask: an
         app that re-dispatches our replayed click a few milliseconds later
         used to drive intercept -> sync -> replay -> intercept forever, so
         the customer never reached checkout at all. MAX_INTERCEPTS is the
         hard backstop - after it the listener stops touching clicks and
         checkout works exactly as if this script were not installed. */
      if (replayingClick || now() < replayGuardUntil) return;
      if (intercepts >= MAX_INTERCEPTS) return;

      var target = event.target;
      if (!target || !target.closest) return;
      var trigger = target.closest(CHECKOUT_SELECTOR);
      if (!trigger) return;

      /* Wallet buttons (shopify-accelerated-checkout and friends) render
         inside a shadow root, so the event retargets to the HOST and
         host.click() does not activate the real button. Preventing the
         default here would leave a dead button with nothing to replay and
         nothing to navigate to. Sync in the background, let the click go. */
      if (trigger.shadowRoot || /-/.test(trigger.tagName || '')) {
        sync({ noRender: true, force: true });
        return;
      }

      intercepts += 1;
      event.preventDefault();
      event.stopPropagation();

      /* The barrier must gate the READ, not only the writes. sync() starts
         with GET /cart.js, and while one of the theme's own cart writes is
         still in flight that read returns the PRE-write cart - the customer
         then checked out with Express selected and no fee at all. Waiting
         first costs nothing that matters: the whole thing is still capped by
         CHECKOUT_MAX_WAIT_MS below. `force` bypasses a temporary pause -
         checkout is the last chance to get the money right. */
      settle(
        foreignSettled(CHECKOUT_MAX_WAIT_MS).then(function () {
          return sync({ noRender: true, force: true });
        }),
        CHECKOUT_MAX_WAIT_MS,
        false
      ).then(function () {
        /* The theme may have re-rendered the cart while we waited, detaching
           the original button. Clicking a detached node does nothing, which
           would look like a dead checkout button. */
        var live = stillInDocument(trigger) ? trigger : document.querySelector(REPLAY_SELECTOR);
        if (!live) {
          window.location.href = '/checkout';
          return;
        }
        replayingClick = true;
        try {
          live.click();
        } catch (error) {
          /* A listener bound to the button threw, so the default action never
             ran. Swallowing that here would leave the customer staring at a
             button that does nothing. */
          log('the replayed checkout click threw; navigating directly.', error);
          window.location.href = '/checkout';
        } finally {
          /* The flag covers a synchronous re-dispatch; the window covers an
             asynchronous one on a later tick. */
          replayGuardUntil = now() + 2000;
          window.setTimeout(function () { replayingClick = false; }, 0);
        }
      });
    },
    true
  );

  /* ---------------------------------------------------------------- boot */

  function init() {
    sync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) sync();
  });

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) sync();
  });
})();
